import { EventEmitter, Injectable, Output } from '@angular/core';
import { AuthHttpService } from './authentication/auth-http.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Client,
  CropFamily,
  CropType,
  DroneService,
  Farm,
  Orchard,
  OrchardCultivar,
  SatelliteDates,
  Survey,
  SurveyFarmOverview,
  User,
  FarmAccess,
  TreeGroup,
  ClientFarm,
  FarmAccessOption,
  FarmSharedWithClient,
  Policy,
  PolicyAccessOption,
  SharedWithClient,
  AgronomyTranslation,
  OrchardWithDisplayName
} from 'SharedModels/app.models';
import {
  ScoutRoute,
  ScoutMission,
  ScoutRouteTag,
  ScoutRouteType,
  PestType,
  PestTypeCropType,
  DiseaseType,
  PestStage,
  DiseaseIntensity,
  DiseaseTypeCropType,
  FruitCountMethod,
} from 'SharedModels/scout.models';
import { FarmInfo, PolicyInfo } from 'SharedModels/app.viewmodels';
import { EventTrackerService } from './event-tracker.service';
import { Observable, forkJoin, of } from 'rxjs';
import { tap, map, catchError, switchMap, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import * as Sentry from '@sentry/browser';
import * as moment from 'moment';
import { Cacheable } from 'ts-cacheable';
import { AgronomyTranslationService } from './agronomy-translation.service';
import { FarmDataType, SurveyLayerName, TreeDataMode } from 'AppServices/maps/models';

const INSURANCE_USER_ROLE_TYPE_ID = 10;

const NEW_PLATFORM_HOME = 'https://app.aerobotics.com/grower/';

export interface AssignToClientOption {
  name: string;
  id: number;
}

@Injectable()
export class DataService {
  // Next url defines the url that the user originally requested
  public nextUrl = null;
  public mobileDevice = false;
  public user: User;
  public staffUser: User = null;
  public staff = false;
  public outsourcedUser = false;
  public client: Client;
  public clients: Client[] = [];
  public farms: Array<Farm>;
  public farmInfo: FarmInfo;
  public policyInfo: PolicyInfo;
  public clientPolicies: Policy[];
  public nonPolicyFarmsForInsuranceUser: (ClientFarm|FarmSharedWithClient)[] = [];
  public activePolicy: Policy;
  public clientFarms: ClientFarm[] = [];
  public orchards: Array<Orchard> = [];
  public orchardCultivars: Array<OrchardCultivar> = [];
  public currentFarmOverview: SurveyFarmOverview[];
  // Orchards for the current active farm
  public farmBoundaries: Array<OrchardWithDisplayName> = [];
  public farmAccess: FarmAccess[] = [];
  public cropFamilies: Array<CropFamily>;
  public cropTypes: Array<CropType>;
  private _satelliteDates: Array<SatelliteDates>;
  // Variables to keep track of the active objects
  public activeFarm: Farm;
  // Farm component which has user "access"
  public activeClientFarm: ClientFarm;
  public activeOrchard: Orchard;
  public orchardHasValidSurvey = false;
  public orchardHasValidHighResSatellite = false;
  public activeSurvey: Survey|null;
  public publicInfo: any;
  public activeDroneService: DroneService;
  public userConfirmed = false;
  // public client = false;
  public currentMode = {mode: 'insights', subMenu: false, subMenuHidden: true};
  public loadingApp = false;
  public lazyLoadingRoute = false;
  public processing: any;
  public tabsRef: any;
  public treeGroups: TreeGroup[] = [];
  public scoutRouteTypes: ScoutRouteType[] = [];
  public scoutRouteTags: ScoutRouteTag[] = [];
  public scoutRoutes: ScoutRoute[];
  public scoutMissions: ScoutMission[];
  public scoutPestTypes: PestType[] = [];
  public scoutPestStages: PestStage[] = [];
  public scoutDiseaseTypes: DiseaseType[] = [];
  public scoutDiseaseIntensities: DiseaseIntensity[] = [];
  public pestTypeCropTypes: PestTypeCropType[] = [];
  public diseaseTypeCropTypes: DiseaseTypeCropType[] = [];
  public fruitCountMethods: FruitCountMethod[] = [];

  // Keep track of the url state
  public state: {
    mode: string, // TODO: Can we more strictly type this, or remove it completely?
    lat: number,
    lng: number,
    zoom: number,
    data: FarmDataType,
    boundary: number,
    survey_id: number,
    date: string,
    layer: string, // TODO: Can we more strictly type this?
    symbolic: SurveyLayerName,
    scoutMission: number,
    scoutRoute: number,
    tree_group_id: number,
    view: TreeDataMode,
    compare_date: string,
    census: boolean
    soil_map_layer: string;
  };

  // Keep track of the previous url
  public previousUrl = null;

  private groupPermissions = [
    { name: 'All Access',
      access: [
        { mode: 'human-intelligence', subMode: 'job-heatmap'},
        { mode: 'processing', subMode: null },
        { mode: 'trees', subMode: null },
        { mode: 'tree-contours', subMode: null },
        { mode: 'payments', subMode: null },
        { mode: 'query', subMode: null },
        { mode: 'clients', subMode: null },
        { mode: 'performance', subMode: null },
        { mode: 'changelog', subMode: null },
        { mode: 'droneservices', subMode: null },
        { mode: 'human-intelligence', subMode: 'job-list'},
        { mode: 'human-intelligence', subMode: 'quality-control'},
        { mode: 'human-intelligence', subMode: 'stitching-issues'},
        { mode: 'human-intelligence', subMode: 'action-errors'},
        { mode: 'human-intelligence', subMode: 'align-dataset-layers'},
        { mode: 'human-intelligence', subMode: 'align-to-previous-dataset'},
        { mode: 'human-intelligence', subMode: 'tree-cleaning'},
        { mode: 'human-intelligence', subMode: 'contour-cleaning'},
        { mode: 'human-intelligence', subMode: 'data-annotation'},
      ]
    },
    { name: 'Agronomist',
      access: [
        { mode: 'human-intelligence', subMode: 'job-list'},
        { mode: 'human-intelligence', subMode: 'data-annotation'},
      ]
    },
    { name: 'Tree Cleaner',
      access: [
        { mode: 'trees', subMode: null },
        { mode: 'human-intelligence', subMode: 'tree-cleaning'},
      ]
    },
    { name: 'Contour Cleaner',
      access: [
        { mode: 'human-intelligence', subMode: 'contour-cleaning'},
      ]
    },
    { name: 'Quality Control',
      access: [
        { mode: 'human-intelligence', subMode: 'quality-control'},
      ]
    },
    { name: 'GCP Alignment',
      access: [
        { mode: 'human-intelligence', subMode: 'align-dataset-layers'},
        { mode: 'human-intelligence', subMode: 'align-to-previous-dataset'},
      ]
    },
    { name: 'Analytics',
      access: [
        { mode: 'query', subMode: null },
        { mode: 'clients', subMode: null },
        { mode: 'performance', subMode: null },
        { mode: 'changelog', subMode: null },
      ]
    },
    { name: 'Payments',
      access: [
        { mode: 'payments', subMode: null },
        { mode: 'query', subMode: null },
        { mode: 'clients', subMode: null },
        { mode: 'changelog', subMode: null },
      ]
    },
    { name: 'Data Operations',
      access: [
        { mode: 'human-intelligence', subMode: 'job-heatmap'},
        { mode: 'processing', subMode: null },
        { mode: 'clients', subMode: null },
        { mode: 'changelog', subMode: null },
        { mode: 'droneservices', subMode: null },
        { mode: 'trees', subMode: null },
        { mode: 'tree-contours', subMode: null },
        { mode: 'human-intelligence', subMode: 'job-list'},
        { mode: 'human-intelligence', subMode: 'quality-control'},
        { mode: 'human-intelligence', subMode: 'align-dataset-layers'},
        { mode: 'human-intelligence', subMode: 'align-to-previous-dataset'},
        { mode: 'human-intelligence', subMode: 'stitching-issues'},
        { mode: 'human-intelligence', subMode: 'action-errors'},
        { mode: 'human-intelligence', subMode: 'tree-cleaning'},
        { mode: 'human-intelligence', subMode: 'contour-cleaning'},
      ]
    },
    { name: 'Drone Operations',
      access: [
        { mode: 'clients', subMode: null },
        { mode: 'droneservices', subMode: null },
      ]
    },
    { name: 'Support and Sales',
      access: [
        { mode: 'clients', subMode: null },
        { mode: 'processing', subMode: null },
        { mode: 'changelog', subMode: null },
        { mode: 'droneservices', subMode: null },
      ]
    }
  ];

  public staffViewablePages: Array<string>;

  public pricing = {
    ZA: {treeHectares: 30, fieldHectares: 15, currency: 'R', treeSubs: 10, fieldSubs: 5},
    US: {treeHectares: 2.5, fieldHectares: 1.25, currency: '$', treeSubs: 1, fieldSubs: 0.5},
    country: 'ZA'
  };
  public country = 'ZA';
  @Output() dataWindow = new EventEmitter<string>();
  public languages = [];
  public selectedLanguage = {
    description: 'English (United States)',
    code: 'en-US',
    flag: 'US.svg'
  };
  @Output() languageChange = new EventEmitter<boolean>();
  public environment = 'www';

  constructor(private authHttp: AuthHttpService, private router: Router, private eventTrackerService: EventTrackerService,
              private route: ActivatedRoute, private agronomyTranslationService: AgronomyTranslationService) {
    this.eventTrackerService.getCountry().subscribe((country) => {
      this.country = country;
    });
  }
  /** Function to determine if the user is an outsoured user or not **/
  determineIfOutsourcedUser() {
    const userGroups = this.user.groups.map(item => item.name);
    if (userGroups.indexOf('OutsourcedUser') !== -1) {
      this.outsourcedUser = true;
    }
  }

  /** Function to determine what the staff member should see **/
  determineStaffAccess() {
    const allPermissions = [];
    // check group
    this.user.groups.forEach(group => {
      const relevantGroup = this.groupPermissions.find(item => item.name === group.name);
      if (typeof relevantGroup !== 'undefined') {
        const groupAccess = relevantGroup.access;
        const modes = groupAccess.map(item => item.mode);
        const subModes = groupAccess.filter(item => item.subMode !== null).map(obj => obj.subMode);
        allPermissions.push.apply(allPermissions, modes);
        allPermissions.push.apply(allPermissions, subModes);
      }
    });
    if (allPermissions.length !== 0) {
      this.staffViewablePages = allPermissions;
    }
  }

  updateCurrentMode(mode, options) {
    const newMode = options.filter((item) => {
      return item.fields.mode === mode;
    })[0];
    if (newMode) {
      this.currentMode = JSON.parse(JSON.stringify(newMode.fields));
    }
  }

  /** Function to navigate to a url **/
  navigateTo(relativeUrl: string): void {
    if (this.nextUrl === null) {
      // If the url includes internal-tools then redirect to internal tools
      if (relativeUrl.includes('internal-tools') && !relativeUrl.includes('localhost') && !relativeUrl.includes('dev')) {
        const domainRerouted = 'https://internal-tools.aerobotics.com';
        window.location.href = `${domainRerouted}${relativeUrl}`;
      } else {
        this.router.navigateByUrl(relativeUrl);
      }
    } else {
      window.location.href = this.nextUrl;
      // Reset the "next url"
      this.nextUrl = null;
    }
  }

  /** Navigate to the correct tool if necessary **/
  navigateToCorrectInternalTool(): void {
    this.determineStaffAccess();
    this.navigateTo('/internal-tools/' + this.staffViewablePages[0]);
  }

  updateUserInCacheAndNavigateToCorrectPage(user: User) {
    this.userConfirmed = true;
    this.user = user;
    Sentry.configureScope((scope) => {
      scope.setUser({
        email: this.user.email,
        id: this.user.id.toString()
      });
    });


    const queryParams = this.route.snapshot.queryParams;
    if (queryParams.hasOwnProperty('redirect_url')) {
      window.location.href = `https://${queryParams.redirect_url}`;
      return
    }

    // Check whether the user is "outsourced" or not
    this.determineIfOutsourcedUser();
    // If staff member route to internal tools otherwise to farms

    if (this.user.is_staff || this.outsourcedUser) {
      this.staff = this.user.is_staff;
      this.navigateToCorrectInternalTool();
    } else if (user.service_provider) {
      // Extract just the farms for the user
      this.fetchAdditionalData();
      // Navigate to the farms page
      this.navigateTo('/aeroview');
    } else {
      window.location.href = NEW_PLATFORM_HOME;
    }
  }

  /** Function for the login api action **/
  loginAction(email: string, password: string, userId?: number): Observable<boolean> {
    return this.authHttp.login(email, password, userId).pipe(
      map((user: User | null) => {
        if (user !== null) {
          this.updateUserInCacheAndNavigateToCorrectPage(user);
          return true;
        }
        return false;
      }),
      catchError((error) => {
        console.log('Login error is: ', error);
        // this.loadingApp = false;
        return of(false);
      })
    );
  }

  public updateActiveClientFarmAndGetLegacyActiveFarm(farmId: number): Farm {
    this.activeClientFarm = this.clientFarms.find(_ => _.farm_id === farmId);
    if (this.activeClientFarm) {
      return {
        id: this.activeClientFarm.farm_id,
        client_id: this.activeClientFarm.client_id,
        name: this.activeClientFarm.farm_name,
        polygon: '',
      };
    }
    return;
  }

  ensureLegalRouteForPolicy(clientId: number, policyId: number): Observable<boolean> {
    return this.getAllPolicyData(clientId).pipe(
      switchMap(() => {
        if (!this.policyInfo) {
          return of(false);
        }
        // Need to make sure that the policy accessed belongs to the policies allocated to the user
        const availablePolicyIds = this.policyInfo.policies.map(_ => _.id);
        if (!availablePolicyIds.includes(policyId)) {
          return of(false);
        }
        return of(true);
      }),
      tap(() => {
        this.activePolicy = this.policyInfo.policies.find(_ => _.id === policyId);
      }),
      switchMap(() => {
        const farmIds = this.activePolicy.grower_farm_ids;
        return this.getOrchardsForFarms(farmIds);
      }),
      tap(() => {
        this.setupFarmBoundariesForPolicy();
      }),
      switchMap(() => {
        return of(true);
      })
    );
  }

  setupFarmBoundariesForPolicy() {
    const orchardIdsFromPolicyOrchards = this.policyInfo.policy_orchards
      .filter(_ => _.policy_id === this.activePolicy.id)
      .map(_ => _.orchard_id);
    // If a policy doesn't cover an entire farm, make sure the orchards don't show up
    this.farmBoundaries = this.farmBoundaries.filter(_ => orchardIdsFromPolicyOrchards.includes(_.id));
    this._updateDisplayNameToIncludePolicyOrchardNames();
  }

  /** A farm / policy "boundary" can either have its name from the underlying orchard or policy orchard. Policy orchard is prioritised **/
  private _updateDisplayNameToIncludePolicyOrchardNames() {
    this.farmBoundaries.forEach((fb) => {
      const policyOrchard = this.policyInfo.policy_orchards.find(_ => _.orchard_id === fb.id);
      if (policyOrchard && policyOrchard.name) {
        fb.display_name = policyOrchard.name;
      } else {
        fb.display_name = fb.name;
      }
    });
  }

  /** Function to ensure that a route is legal **/
  ensureLegalRoute(farmId: number): Observable<boolean> {
    // Make sure the farm has the correct farm and client id
    this.activeFarm = this.updateActiveClientFarmAndGetLegacyActiveFarm(farmId);

    if (this.activeClientFarm) {
      return this.getOrchardsForFarms([farmId]).pipe(
        map(() => {
          return true;
        }),
        catchError(() => {
          return of(false);
        })
      );
    } else {
      this.router.navigateByUrl('/log-in?auto_login=false');
      this.loadingApp = false;
      return of(false);
    }
  }

  getOrchardsForFarms(farmIds: number[]): Observable<Orchard[]> {
    const farmIdsString = farmIds.join(',');
    // Just choose the first farm in the list and make that activeFarm. This is so that the insurance functionality can work
    const farmId = farmIds[0];
    // First attempt to get cached orchards by farm id
    const cachedOrchardsForFarmId = this.orchards.filter(orchard => farmIds.includes(orchard.farm_id));
    // If there are cached orchards for the farm id, return without fetching orchards from the server
    if (cachedOrchardsForFarmId.length > 0) {
      this.farmBoundaries = cachedOrchardsForFarmId;
      return of(cachedOrchardsForFarmId);
    }
    // Make sure there are crop types too then fork join a request to get the farms
    if (typeof this.cropTypes === 'undefined' || typeof this._satelliteDates === 'undefined') {
      return forkJoin([
        this.authHttp.get('gateway/croptypes/'),
        this.authHttp.get('gateway/orchards/?farm_id__in=' + farmIdsString),
        this.authHttp.get('gateway/satellitedates/?farm_id__in=' + farmIdsString),
      ])
        .pipe(
          map((data: [Array<CropType>, Array<Orchard>, Array<SatelliteDates>]) => {
            this.cropTypes = data[0];
            // Update the farm boundaries (orchards)
            this.farmBoundaries = data[1];
            // Add fetched farm boundaries to orchards cache
            this.orchards = this.orchards.concat(this.farmBoundaries);
            this.activeFarm = this.updateActiveClientFarmAndGetLegacyActiveFarm(farmId);
            // If the satellite dates have not been found yet then update them
            if (typeof this._satelliteDates === 'undefined') {
              this._satelliteDates = data[2];
            }
            return this.farmBoundaries;
          })
        );
    } else {
      return this.authHttp.get('gateway/orchards/?farm_id__in=' + farmIdsString)
        .pipe(
          map((orchards: Orchard[]) => {
            // Update the farm boundaries (orchards)
            this.farmBoundaries = orchards;
            // Add fetched farm boundaries to orchards cache
            this.orchards = this.orchards.concat(this.farmBoundaries);
            this.activeFarm = this.updateActiveClientFarmAndGetLegacyActiveFarm(farmId);
            return this.farmBoundaries;
          })
      );
    }
  }

  getOrchardCultivars(): Observable<OrchardCultivar[]> {
    // If there are cached orchardCultivars, return without fetching from the server
    if (this.orchardCultivars.length > 0) {
      return of(this.orchardCultivars);
    }

    return this.authHttp.get('gateway/orchardcultivars').pipe(
      tap((orchardCultivars: OrchardCultivar[]) => { this.orchardCultivars = orchardCultivars; })
    ) as Observable<OrchardCultivar[]>;
  }

  /** Function to iterate through a clients list and extract the flattened hierarchy **/
  extractClientsFromHierarchy(clientsList, clients) {
    for (let i = 0; i < clients.length; i++) {
      const client = clients[i];
      clientsList.push(client);
      if (client.children.length > 0) {
        this.extractClientsFromHierarchy(clientsList, client.children);
      }
    }
  }

  /** Function to fetch the additional data of the client **/
  fetchAdditionalData() {
    // Determine whether the user is a client or not
    if (this.user.is_staff === false) {
      // Get the client id based on the user id matching in the clients
      const userClient = this.user.clients.filter(item => {
        return item.user_id === this.user.id;
      });
      // Update the client id
      if (userClient.length > 0) {
        this.client = userClient[0];
      } else {
        this.client = null;
      }
    }
    if (this.client !== null) {
      this.fetchClientData();
    } else {
      // Otherwise at the very least get the crop types so that a service provider can still upload data
      this.authHttp.get('gateway/croptypes/').subscribe((cropTypes: Array<CropType>) => {
        // Update the crop types
        this.cropTypes = cropTypes;
      });
    }
  }

  /** Get the list of people who have access to this farm so that you can assign a scout to one of them **/
  public _getAvailableClientsToAssignTo(): AssignToClientOption[] {
    // The clients which can be assigned to the scout must either be the main user or clients which belong to the specific farm
    return [
      // The owner
      {
        id: this.activeClientFarm.client_id,
        name: this.activeClientFarm.client_name
      },
      // Everyone else the farm is shared with
      ...this.activeClientFarm.shared_with_clients.map(_ => {
        return {
          id: _.shared_with_client_id,
          name: (_.shared_with_client_name && _.shared_with_client_name.trim()) ?
            _.shared_with_client_name : _.shared_with_client_email
        };
      })
    ].sort((a, b) => {
      return a.name.toLowerCase() > b.name.toLowerCase() ? 1 :
        a.name.toLowerCase() === b.name.toLowerCase() ? 0 : -1
    });
  }

  public getCropTypes(): Observable<CropType[]> {
    if (this.cropTypes) {
      return of(this.cropTypes);
    }
    return this.authHttp.get('gateway/croptypes/').pipe(
      tap((cropTypes: CropType[]) => {
        this.cropTypes = cropTypes;
      })
    );
  }

  /** Function to fetch all the client relevent data **/
  fetchClientData() {
    // Load agronomy translations.
    this.agronomyTranslationService.fetchPestAndDiseaseTranslations().subscribe(() => {});
    const farmIds = this.clientFarms.map(obj => (obj.farm_id)).join(',');
    // Make a get for all the satellite dates for the client
    this.authHttp.get('gateway/satellitedates/?farm_id__in=' + farmIds).subscribe((satelliteDates: Array<SatelliteDates>) => {
      // Update the orchards variable
      this._satelliteDates = satelliteDates;
    }, () => {
      console.log('Error fetching satellite dates');
    });
    this.getCropTypes().subscribe(() => {});
    // Make a get for all the crop types
    this.authHttp.get('gateway/cropfamilies/').subscribe((cropFamilies: CropFamily[]) => {
      this.cropFamilies = cropFamilies;
    });
    // Get the public info
    this.loadPublicCredentials();
    this.authHttp.get('scouting/scoutroutetags/').subscribe((scoutRouteTags: ScoutRouteTag[]) => {
      this.scoutRouteTags = scoutRouteTags;
    });
    this.authHttp.get('scouting/scoutroutetypes/').subscribe((scoutRouteTypes: ScoutRouteType[]) => {
      this.scoutRouteTypes = scoutRouteTypes;
    });
    forkJoin([
      this.authHttp.get('scouting/pesttypes/'),
      this.authHttp.get('scouting/diseasetypes/')
    ]).subscribe(([pests, diseases]: [PestType[], DiseaseType[]]) => {
      this.scoutPestTypes = pests;
      this.scoutDiseaseTypes = diseases;
      this.translateAllPestAndDiseaseTypes();
    });
    this.authHttp.get('scouting/pesttypecroptypes/').subscribe((pestsTypeCropTypes: PestTypeCropType[]) => {
      this.pestTypeCropTypes = pestsTypeCropTypes;
    });
    this.authHttp.get('scouting/diseasetypecroptypes/').subscribe((diseaseTypeCropTypes: DiseaseTypeCropType[]) => {
      this.diseaseTypeCropTypes = diseaseTypeCropTypes;
    });
    this.authHttp.get('scouting/diseaseintensities/').subscribe((diseaseIntensities: DiseaseIntensity[]) => {
      this.scoutDiseaseIntensities = diseaseIntensities;
    });
    this.authHttp.get('scouting/peststages/').subscribe((pestStages: PestStage[]) => {
      this.scoutPestStages = pestStages;
    });
    // Get the farm access available for the client
    this.authHttp.get('gateway/farmaccess/?client_id=' + this.client.id).subscribe((farmAccess: FarmAccess[]) => {
      this.farmAccess = farmAccess;
    });
  }

  @Cacheable()
  public loadPublicCredentials() {
    // Get the public info
    return this.authHttp.get('gateway/auth/get_public_credentials/').pipe(
      tap((data) => {
        this.publicInfo = data;
      })
    );
  }

  logoutAction(): void {
    // Still deciding whether to have this in or not
    // This get request removes any Cognito cookies
    this.authHttp.get('gateway/auth/logout/').pipe(
      finalize(() => {
        localStorage.removeItem('aeroUser');
        if (!window.location.href.includes('/log-in')) {
          this.router.navigateByUrl('/log-in?auto_login=false');
        }
      })
    ).subscribe(() => {});
  }

  /** Function which updates the little chat bot on the right with the user data, by getting the secure credentials first **/
  updateBeaconInformation(user: User): void {
    this.authHttp.get('gateway/auth/beacon_user_hash/').subscribe((data: {user_hash: string}) => {
      (<any>window).Beacon('identify', {
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        signature: data.user_hash,
      });
    }, () => {});
  }

  /** Function to update all the properties in the data service relating to the language selected **/
  updateSelectedLanguage() {
    const language = this.languages.find(item => item.code === this.selectedLanguage.code);
    this.selectedLanguage = {
      flag: language.code.split('-')[1] + '.svg',
      description: language.name,
      code: language.code
    };
    this.languageChange.emit(true);
    // Update the locale of the 'aeroUser'
    localStorage.setItem('aeroLocale', language.code);
    // Update all instances of moment to use this language
    moment.locale(language.code.split('-')[0]);
    // Retranslate pest and disease types
    if (this.client) {
      this.translateAllPestAndDiseaseTypes();
    }
  }

  // Parameters: Using the any type because fieldsToUpdate is dynamic
  bulkUpdateOrchards(orchards: Array<Partial<Orchard>>, fieldsToUpdate: string[]) {
   return this.authHttp.post('gateway/orchards/bulk_update/', {
      'orchards': orchards,
      'fields_to_update': fieldsToUpdate
    }).pipe(
      tap( (orchardsFromServer: any[]) => {
        if (this.farmBoundaries) {
          this.updateLocalValues(this.farmBoundaries, orchardsFromServer, fieldsToUpdate);
        }
        if (this.orchards) {
          this.updateLocalValues(this.orchards, orchardsFromServer, fieldsToUpdate);
        }
        if (this.activeDroneService) {
          this.updateLocalValues(this.activeDroneService.orchards, orchardsFromServer, fieldsToUpdate);
        }
      })
    );
  }

  // Parameters: Using the any type because fieldsToUpdate is dynamic
  private updateLocalValues(localOrchards: any[], serverOrchards: any[], fieldsToUpdate: string[]) {
    serverOrchards.forEach( (serverOrchard: any) => {
      const associatedLocalOrchard = localOrchards.find(_ => _.id === serverOrchard.id);
      if (associatedLocalOrchard) {
        fieldsToUpdate.forEach(field => {
          associatedLocalOrchard[field] = serverOrchard[field];
        });
      }
    });
  }

  /** Function to authenticate an orchard and update the relevant cookies **/
  authenticateOrchard(orchardId, surveyId): Observable<boolean> {
    // Default the post data to authenticate on an orchard level
    let postData: any = { orchard_id: orchardId };
    // If the request is coming from an outsourced user then authenticate on the survey level
    if (this.outsourcedUser && surveyId !== null) {
      postData = { survey_id: surveyId };
    }
    if (environment.envName === 'devLocal') {
      return of(true);
    } else {
      return this.authHttp.postWithCredentials('gateway/s3helpers/authorize_orchard_data/', postData)
        .pipe(
          map(() => true),
          catchError(() => of(false))
      );
    }
  }

  /** Function which takes in an array of environments and returns whether or not the set environment is in that list **/
  public featureToggleEnabled(environments: string[]): boolean {
    return environments.indexOf(this.environment) !== -1;
  }

  public featureToggleEnabledForClient(clientIds: number[]): boolean {
    return clientIds.indexOf(this.client.id) !== -1;
  }

  public featureToggleEnabledForOwner(farmOwnerIds: number[]): boolean {
    const ownersOfFarmsWhereClientHasAccess = this.clientFarms.map(f => f.client_id);
    return farmOwnerIds.some(c_id => ownersOfFarmsWhereClientHasAccess.indexOf(c_id) !== -1);
  }

  clientHasReadAccessOnly(): boolean {
    if (this.activePolicy) {
      if (this.activePolicy.aip_client_id === this.client.id) {
        return false;
      }
      const readOnlyAccess = this.activePolicy.shared_with_clients
        .find(_ => _.shared_with_client_id === this.client.id && _.access === PolicyAccessOption.Read);
      return readOnlyAccess !== undefined;
    }
    return this.activeClientFarm && this.activeClientFarm.access === FarmAccessOption.Read;
  }

  public clientHasInsuranceProviderRoleType(): boolean {
    return this.client.role_type_id === INSURANCE_USER_ROLE_TYPE_ID;
  }

  @Cacheable()
  public getAllPolicyData(clientId): Observable<any> {
    return this.getPolicyInfo(clientId).pipe(
      tap(() => {
        this.generateClientPolicies();
      })
    ) as Observable<any>;
  }

  public getPolicyInfo(clientId): Observable<PolicyInfo> {
    return this.authHttp.get(`insurance/policyaccess/get_policy_list_information/?client_id=${clientId}`).pipe(
      tap((policyInfo: PolicyInfo) => {
        this.policyInfo = policyInfo;
      })
    ) as Observable<PolicyInfo>;
  }

  public generateClientPolicies() {
    const clientPolicies: Policy[] = [];
    this.policyInfo.policies.forEach(policy => {
      const policyOrchards = this.policyInfo.policy_orchards.filter(_ => _.policy_id === policy.id);
      const clientPolicy: Policy = Object.assign(policy, {
        number_of_orchards: policyOrchards.length,
        total_hectares: policyOrchards.map(_ => _.hectares).reduce((a, b) => a + b, 0),
        shared_with_clients: this.policyInfo.policies_shared_with
          .filter(_ => _.policy_id === policy.id && _.shared_with_client_id !== policy.aip_client_id)
      });
      clientPolicies.push(clientPolicy);
    });
    this.clientPolicies = clientPolicies;
  }

  public getSatelliteDatesForFarmOrPolicy(): SatelliteDates {
    if (this.activePolicy) {
      return this._satelliteDates.find(_ => _.policy_id === this.activePolicy.id);
    } else if (this.activeFarm) {
      return this._satelliteDates.find(_ => _.farm_id === this.activeFarm.id);
    }
  }

  public addNewFarmOrPolicySatelliteDates(satelliteDates: SatelliteDates) {
    this._satelliteDates = this._satelliteDates.concat(satelliteDates);
  }

  public updateSatelliteDatesById(satelliteDates: SatelliteDates) {
    const satelliteDatesIndex = this._satelliteDates.findIndex(item => item.id === satelliteDates.id);
    this._satelliteDates[satelliteDatesIndex] = satelliteDates;
  }

  public getFarmAvailableClientsToShareWith() {
    return this.formatAvailableClientsToShareWith([].concat.apply([], this.clientFarms.map(_ => _.shared_with_clients)));
  }

  public getPolicyAvailableClientsToShareWith() {
    return this.formatAvailableClientsToShareWith(this.policyInfo.policies_shared_with);
  }

  public formatAvailableClientsToShareWith(sharedWithClients: SharedWithClient[]) {
    const availableClientsToShareWith = [].concat.apply([], sharedWithClients)
      .sort((a, b) => a.shared_with_client_name > b.shared_with_client_name ? 1 : -1);

    const resArray = [];
    availableClientsToShareWith.forEach((item) => {
      const index = resArray.findIndex(x => x.shared_with_client_email === item.shared_with_client_email);
      if (index === -1) {
        resArray.push(item);
      }
    });
    return resArray;
  }

  public translateAllPestAndDiseaseTypes() {
    // Load pest and disease translations.
    this.agronomyTranslationService.fetchPestAndDiseaseTranslations().subscribe((translations: AgronomyTranslation[]) => {
      this.scoutPestTypes = this.scoutPestTypes.map(pestType => {
        const pestTranslations = translations.filter(translation => translation.category === 'pest');
        pestType.name = this.agronomyTranslationService
          .translateTypeName(pestType.name, pestType.id, pestTranslations, this.selectedLanguage.code);
        return pestType;
      });
      this.scoutDiseaseTypes = this.scoutDiseaseTypes.map(diseaseType => {
        const diseaseTranslations = translations.filter(translation => translation.category === 'disease');
        diseaseType.name = this.agronomyTranslationService
          .translateTypeName(diseaseType.name, diseaseType.id, diseaseTranslations, this.selectedLanguage.code);
        return diseaseType;
      });
    });
  }

  public initialiseMonitorState() {
    this.state = {
      mode: 'monitor',
      lat: -33.5,
      lng: 18.8,
      zoom: 10,
      data: 'drone',
      boundary: null,
      survey_id: null,
      date: null,
      layer: 'visible',
      symbolic: null,
      scoutMission: null,
      scoutRoute: null,
      tree_group_id: null,
      view: null,
      compare_date: null,
      census: false,
      soil_map_layer: null
    };
  }

}
